import axios from "axios";
import { apiUrl } from "@/env";
import {
  UserProfile,
  UserProfileUpdate,
  UserProfileCreate,
  Miner,
  MinerUpdate,
  Share,
  Wallet,
  WalletUpdate,
  WalletCreate,
  Currency,
  WalletBalance,
  WithdrawState,
  WithdrawCreate,
  WithdrawUpdate,
  Withdraw,
  Price,
  WithdrawSettle,
  Income,
  CompanyCreate,
  Company,
  Asset
} from "./interfaces";

function authHeaders(token: string) {
  return {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };
}

export const api = {
  async logInGetToken(username: string, password: string) {
    const params = new URLSearchParams();
    params.append("username", username);
    params.append("password", password);
    return axios.post(`${apiUrl}/api/v1/login/access-token`, params);
  },
  async logInGetTokenWithSMS(mobile: string, token: string) {
    const params = new URLSearchParams();
    params.append("mobile", mobile);
    params.append("token", token);
    return axios.post(`${apiUrl}/api/v1/login/sms-access-token`, params);
  },
  async logInSendSMSToken(mobile: string) {
    const params = new URLSearchParams();
    params.append("mobile", mobile);
    return axios.post(`${apiUrl}/api/v1/login/send-sms-token`, params);
  },
  async getMe(token: string) {
    return axios.get<UserProfile>(
      `${apiUrl}/api/v1/users/me`,
      authHeaders(token)
    );
  },
  async updateMe(token: string, data: UserProfileUpdate) {
    return axios.put<UserProfile>(
      `${apiUrl}/api/v1/users/me`,
      data,
      authHeaders(token)
    );
  },
  async getUsers(token: string) {
    return axios.get<UserProfile[]>(
      `${apiUrl}/api/v1/users/`,
      authHeaders(token)
    );
  },
  async updateUser(token: string, userId: number, data: UserProfileUpdate) {
    return axios.put<UserProfile>(
      `${apiUrl}/api/v1/users/${userId}`,
      data,
      authHeaders(token)
    );
  },
  async createUser(token: string, data: UserProfileCreate) {
    const response = axios.post<UserProfile>(
      `${apiUrl}/api/v1/users/`,
      data,
      authHeaders(token)
    );
    return response;
  },
  async passwordRecovery(email: string) {
    return axios.post(`${apiUrl}/api/v1/password-recovery/${email}`);
  },
  async resetPassword(password: string, token: string) {
    return axios.post(`${apiUrl}/api/v1/reset-password/`, {
      newPassword: password,
      token
    });
  },
  async getMiners(token: string) {
    return axios.get<Miner[]>(`${apiUrl}/api/v1/miners/`, authHeaders(token));
  },
  async getMiner(token: string, minerId: number, details: boolean) {
    const miner = axios.get<Miner>(
      `${apiUrl}/api/v1/miners/${minerId}?details=${details}`,
      authHeaders(token)
    );
    console.log(miner);
    return miner;
  },
  async updateMiner(token: string, minerId: number, data: MinerUpdate) {
    return axios.put<Miner>(
      `${apiUrl}/api/v1/miners/${minerId}`,
      data,
      authHeaders(token)
    );
  },
  async getShares(token: string) {
    return axios.get<Share[]>(
      `${apiUrl}/api/v1/shares/?limit=1000`,
      authHeaders(token)
    );
  },
  async getSharesByMinerId(
    token: string,
    minerId: number,
    limit: number,
    offset: number
  ) {
    return axios.get<Share[]>(
      `${apiUrl}/api/v1/shares/by_miner/${minerId}?skip=${offset}&limit=${limit}&own=false&asc=true`,
      authHeaders(token)
    );
  },
  async getWallets(token: string, with_balance: boolean) {
    return axios.get<Wallet[]>(
      `${apiUrl}/api/v1/wallets/?with_balance=${with_balance}`,
      authHeaders(token)
    );
  },
  async updateWallet(token: string, walletId: number, data: WalletUpdate) {
    return axios.put<Wallet>(
      `${apiUrl}/api/v1/wallets/${walletId}`,
      data,
      authHeaders(token)
    );
  },
  async createWallet(token: string, data: WalletCreate) {
    const response = axios.post<Wallet>(
      `${apiUrl}/api/v1/wallets/`,
      data,
      authHeaders(token)
    );
    console.log(response);
    return response;
  },
  async getWithdraws(token: string) {
    return axios.get<Withdraw[]>(
      `${apiUrl}/api/v1/withdraws/`,
      authHeaders(token)
    );
  },
  async updateWithdraw(
    token: string,
    withdrawId: number,
    data: WithdrawUpdate
  ) {
    return axios.put<Withdraw>(
      `${apiUrl}/api/v1/withdraws/${withdrawId}`,
      data,
      authHeaders(token)
    );
  },
  async settleWithdraw(
    token: string,
    withdrawId: number,
    data: WithdrawSettle
  ) {
    return axios.put<Withdraw>(
      `${apiUrl}/api/v1/withdraws/settle_base64/${withdrawId}`,
      data,
      authHeaders(token)
    );
  },
  async confirmWithdraw(token: string, withdrawId: number) {
    return axios.put<Withdraw>(
      `${apiUrl}/api/v1/withdraws/confirm/${withdrawId}`,
      null,
      authHeaders(token)
    );
  },
  async revertWithdraw(token: string, withdrawId: number) {
    return axios.put<Withdraw>(
      `${apiUrl}/api/v1/withdraws/revert/${withdrawId}`,
      null,
      authHeaders(token)
    );
  },
  async createWithdraw(token: string, data: WithdrawCreate) {
    const response = axios.post<Withdraw>(
      `${apiUrl}/api/v1/withdraws/`,
      data,
      authHeaders(token)
    );
    console.log(response);
    return response;
  },
  async getCurrencies() {
    const response = axios.get<Currency[]>(
      `${apiUrl}/api/v1/wallets/currencies`
    );
    console.log(response);
    return response;
  },
  async getPrices() {
    const response = axios.get<Price[]>(`${apiUrl}/api/v1/withdraws/prices`);
    console.log(response);
    return response;
  },
  async getWithdrawStates() {
    const response = axios.get<WithdrawState[]>(
      `${apiUrl}/api/v1/withdraws/states`
    );
    console.log(response);
    return response;
  },
  async getBalance(token: string, walletId: number) {
    const response = axios.get<WalletBalance>(
      `${apiUrl}/api/v1/wallets/walletbalance/${walletId}`,
      authHeaders(token)
    );
    console.log(response);
    return response;
  },
  async getRecieptImage(token: string, withdrawId: number) {
    const response = axios.get<Blob>(
      `${apiUrl}/api/v1/withdraws/reciept/${withdrawId}`,
      {
        ...authHeaders(token),
        ...{ responseType: "blob" } //important
      }
    );
    console.log(response);
    return response;
  },

  async getIncomes(token: string) {
    const response = axios.get<Income[]>(
      `${apiUrl}/api/v1/incomes/?own=false&skip=0&asc=false`,
      {
        ...authHeaders(token)
      }
    );
    console.log(response);
    return response;
  },

  async createCompany(token: string, data: CompanyCreate) {
    const response = axios.post<Company[]>(
      `${apiUrl}/api/v1/companies/`,
      data,
      {
        ...authHeaders(token)
      }
    );
    console.log(response);
    return response;
  },
  async getCompanies(token: string, own: boolean) {
    const response = axios.get<Company[]>(
      `${apiUrl}/api/v1/companies/?own=${own}`,
      {
        ...authHeaders(token)
      }
    );
    return response;
  },
  async getCompany(token: string, own: boolean, id: number) {
    const response = axios.get<Company[]>(
      `${apiUrl}/api/v1/companies/?id=${id}&own=${own}`,
      {
        ...authHeaders(token)
      }
    );
    return response;
  },
  async deleteCompany(token: string, own: boolean, id: number) {
    return axios.delete<Company[]>(
      `${apiUrl}/api/v1/companies/${id}`,
      {
        ...authHeaders(token)
      }
    );
  },
  async getLicence(token: string) {
    return axios.get<any>(`${apiUrl}/api/v1/assets/by_name/license`, {
      ...authHeaders(token)
    });
  },
  async createAsset(
    token: string,
    userId: number,
    isPublic: boolean,
    data: Asset
  ) {
    return axios.post<any>(
      `${apiUrl}/api/v1/assets/?owner_id=${userId}&public=${isPublic}`,
      data,
      {
        ...authHeaders(token)
      }
    );
  },
  async updateAsset(token: string, id: number, data: Asset) {
    return axios.put<any>(`${apiUrl}/api/v1/assets/${id}`, data, {
      ...authHeaders(token)
    });
  },
  async getAsset(token: string, name: string) {
    return axios.get<any>(`${apiUrl}/api/v1/assets/by_name/${name}`, {
      ...authHeaders(token)
    });
  },
  async addReferral(token: string, referral: string) {
    return axios.put<any>(`${apiUrl}/api/v1/users/add_referral_code`, referral, {
      ...authHeaders(token)
    });
  },
  async getAppInfo(token: string) {
    return axios.get<any>(`${apiUrl}/api/v1/assets/app_info/`, {
      ...authHeaders(token)
    });
  }
};
